/**
 * Single source of truth for every public page: the router-facing SEO hook,
 * the build-time prerender (static <head>, sitemap.xml, llms.txt) all read
 * from here, so adding a page means adding one entry.
 */

export const SITE_NAME = "Null Autos";
export const ORIGIN = "https://null.autos";
export const OG_IMAGE = `${ORIGIN}/null/og-image.jpg`;
export const OG_IMAGE_ALT =
  "Null Autos: Ship automotive software faster, beside a glass vehicle cockpit";
export const LOGO = `${ORIGIN}/null/logo.white.svg`;
export const SAME_AS = [
  "https://gitlab.com/null-autos",
  "https://linkedin.com/company/null-autos",
];
export const SITE_SUMMARY =
  "Null Autos is the CI/CD platform for software-defined vehicles. Teams build, test, and deploy Android Automotive OS (AAOS) software with burst build compute, browser-based cloud emulators, remote development environments, and behavioral testing, all integrated with GitLab.";

export type PageKind = "home" | "product" | "solution" | "company" | "legal" | "hidden";

export type PageMeta = {
  path: string;
  /** Full <title>; kept under ~60 characters so it isn't truncated in results. */
  title: string;
  /** Meta description; ~150-160 characters. */
  description: string;
  /** Breadcrumb / llms.txt link label. */
  label: string;
  kind: PageKind;
  /** Sitemap priority; omitted pages (noindex) are left out of the sitemap. */
  priority?: number;
  noindex?: boolean;
};

export const PAGES: readonly PageMeta[] = [
  {
    path: "/",
    title: "Null Autos | CI/CD for Software-Defined Vehicles",
    description:
      "Null Autos is the CI/CD platform for software-defined vehicles. Build, test, and deploy Android Automotive software with burst compute, cloud emulators, and GitLab-native workflows.",
    label: "Home",
    kind: "home",
    priority: 1.0,
  },
  {
    path: "/platform",
    title: "Android Automotive Development Platform | Null Autos",
    description:
      "Everything you need to build, test, and deploy infotainment software: multi-cloud infrastructure, CI/CD integration, and automotive-specific tooling in one platform.",
    label: "Platform",
    kind: "product",
    priority: 0.9,
  },
  {
    path: "/products/android-builds",
    title: "Faster Android Automotive (AAOS) Builds | Null Autos",
    description:
      "Speed up AAOS builds with burst compute provisioned on demand, intelligent filesystem snapshots, and GitLab CI templates for instant integration. Stop waiting for builds.",
    label: "Android Builds",
    kind: "product",
    priority: 0.8,
  },
  {
    path: "/products/emulators",
    title: "Cloud Android Automotive Emulators in Browser | Null Autos",
    description:
      "Connect to running Android Automotive emulators instantly from a web browser, with multi-display support, GPS simulation, VHAL controls, and real-time debugging.",
    label: "Web Emulators",
    kind: "product",
    priority: 0.8,
  },
  {
    path: "/products/remote-dev",
    title: "Remote Development for Android Automotive | Null Autos",
    description:
      "A full IDE in the browser via GitLab Workspaces. No local tools required. Onboard automotive developers in minutes and build and test code from anywhere.",
    label: "Remote Development",
    kind: "product",
    priority: 0.8,
  },
  {
    path: "/products/testing",
    title: "Automated Behavioral Testing for AAOS | Null Autos",
    description:
      "Extensible behavioral testing for verifying requirements and testing Android Automotive applications. Product owners write specs; CI validates them automatically.",
    label: "Automated Testing",
    kind: "product",
    priority: 0.8,
  },
  {
    path: "/solutions/engineering-leaders",
    title: "Automotive DevOps for Engineering Leaders | Null Autos",
    description:
      "Cut idle build infrastructure, accelerate your Android Automotive pipeline, and onboard infotainment teams anywhere in the world.",
    label: "For Engineering Leaders",
    kind: "solution",
    priority: 0.7,
  },
  {
    path: "/solutions/developers",
    title: "Infotainment Development Without the Bench | Null Autos",
    description:
      "Fast builds, cloud emulators, and browser-based IDEs that integrate with GitLab. Ship Android Automotive infotainment code and focus on code, not infrastructure.",
    label: "For Developers",
    kind: "solution",
    priority: 0.7,
  },
  {
    path: "/contact",
    title: "Contact Us & Book a Demo | Null Autos",
    description:
      "Schedule a demo, ask a question, or tell us about your automotive software development challenges. We'll get back to you quickly.",
    label: "Contact",
    kind: "company",
    priority: 0.6,
  },
  {
    path: "/privacy",
    title: "Privacy Policy | Null Autos",
    description:
      "How we collect, use, and protect your information when you visit the null.autos website.",
    label: "Privacy Policy",
    kind: "legal",
    priority: 0.2,
  },
  {
    path: "/terms",
    title: "Terms of Use | Null Autos",
    description:
      "The terms and conditions that govern your use of the null.autos marketing website.",
    label: "Terms of Use",
    kind: "legal",
    priority: 0.2,
  },
  {
    path: "/mb/feedback",
    title: "Workshop Feedback | Null Autos",
    description: "Help us tailor the workshop to your needs.",
    label: "Workshop Feedback",
    kind: "hidden",
    noindex: true,
  },
];

export const NOT_FOUND: PageMeta = {
  path: "/404",
  title: "Page Not Found | Null Autos",
  description: "The page you were looking for doesn't exist.",
  label: "Not Found",
  kind: "hidden",
  noindex: true,
};

export function pageMeta(path: string): PageMeta {
  return PAGES.find((p) => p.path === path) ?? NOT_FOUND;
}
